import { z } from 'zod';
import { CampaignAttributionSchema } from '../campaign-attribution.js';
import { EntityTypeSchema } from './account.js';
import { PaginationQuerySchema, LowercaseUuidSchema, base64FileContent, createPaginatedResponseSchema, saPhoneNumberSchema, saVatNumberSchema, writeWarningsFields, } from './common.js';
import { nullableText } from '../sanitised-text.js';
// =============================================================================
// Feedback
// =============================================================================
export const FeedbackTypeSchema = z.enum(['bug', 'idea', 'other']);
/** Allowed MIME types for feedback attachments */
export const ALLOWED_FEEDBACK_MIME_TYPES = [
    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    // Documents
    'application/pdf',
    // Text / debugging artifacts
    'text/plain',
    'text/csv',
    'text/markdown',
    'application/json',
    'text/javascript',
    'application/x-yaml',
    'text/yaml',
];
export const FeedbackAttachmentSchema = z.object({
    /** Original filename (for display only - never used as a storage key) */
    filename: z.string().min(1).max(255),
    /** MIME type - validated against allowlist server-side */
    mimeType: z.string(),
    /** Base64-encoded file content. Tagged fileContent: true so the MCP adapter strips it from the LLM-visible schema. */
    data: base64FileContent(),
});
export const FeedbackSourceSchema = z.enum(['WEB_APP', 'CHAT']);
export const SubmitFeedbackInputSchema = z.object({
    message: z.string().min(10, 'Message must be at least 10 characters').max(2000, 'Message must be 2000 characters or less'),
    type: FeedbackTypeSchema.default('other'),
    source: FeedbackSourceSchema,
    /** Up to 3 file attachments (images, PDFs, text/debug files) */
    attachments: z.array(FeedbackAttachmentSchema).max(3, 'Maximum 3 attachments').default([]),
});
export const SubmitFeedbackResponseSchema = z.object({ success: z.boolean() });
export const LeadSourceSchema = z.enum(['landing-page', 'referral', 'event', 'linkedin', 'web']);
export const LeadStatusSchema = z.enum(['NEW', 'CONTACTED', 'INTERESTED', 'PAYING', 'DORMANT']);
export const DirectMarketingConsentSchema = z.enum(['OPTED_IN', 'OPTED_OUT']);
// =============================================================================
// Lead
// =============================================================================
export const LeadNoteEntrySchema = z.object({
    createdAt: z.iso.datetime(),
    body: z.string(),
    authorName: z.string().nullable().optional(),
});
export const LEAD_MESSAGE_MAX_LENGTH = 2000;
/**
 * Optional free text a visitor leaves on the contact form. Blank collapses to
 * null. Over HTTP the length cap is enforced by AJV, whose generic message
 * wins over any Zod `maxMessage`, so the form caps input with `maxlength`
 * and this is the backstop for other callers.
 */
export const LeadMessageSchema = nullableText(LEAD_MESSAGE_MAX_LENGTH);
export const CreateLeadInputSchema = z.object({
    email: z.email().optional(),
    contactName: z.string().optional(),
    // The api-framework re-parses `req.body` through this real Zod schema
    // (`parseOrValidationError`, not just AJV/z.toJSONSchema), so this
    // transform DOES run for a real request: `phone` arrives at the service
    // already canonicalised to +27XXXXXXXXX. See
    // apps/backend/platform/src/api/http/api-framework.ts.
    phone: saPhoneNumberSchema.optional(),
    message: LeadMessageSchema,
    // Schema-optional so a bare `{ email, source }` body still parses; the
    // superRefine below requires this to be exactly `true`.
    privacyPolicyConsent: z.boolean().optional(),
    source: LeadSourceSchema.default('landing-page'),
    // Optional so a body without campaign parameters is unchanged.
    attribution: CampaignAttributionSchema.optional(),
}).superRefine((val, ctx) => {
    // This DOES run at the HTTP boundary (see the phone-field comment above):
    // a violation surfaces as a 400 VALIDATION_ERROR with this message in
    // `error.details[]`, not as the service's own badRequest().
    if (val.email == null && val.phone == null) {
        ctx.addIssue({
            code: "custom",
            path: ['email'],
            message: 'Please provide an email address or a phone number so we can reach you.',
        });
    }
    if (val.privacyPolicyConsent !== true) {
        ctx.addIssue({
            code: "custom",
            path: ['privacyPolicyConsent'],
            message: 'Please agree to the privacy policy.',
        });
    }
});
export const CreateLeadResponseSchema = z.object({
    id: z.string(),
    email: z.string().nullable(),
    source: z.string(),
    createdAt: z.iso.datetime(),
});
// =============================================================================
// Super Admin
// =============================================================================
export const SuperAdminLeadSchema = z.object({
    id: z.string(),
    email: z.email().nullable(),
    profileUrl: z.url().nullable(),
    source: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    contactName: z.string().nullable(),
    phone: z.string().nullable(),
    message: z.string().nullable(),
    status: z.string(),
    sourceDetail: z.string().nullable(),
    // businessEntityId is the raw FK - still present for update/link operations.
    businessEntityId: z.string().nullable(),
    // businessEntity is the nested object included when the lead has a linked entity.
    businessEntity: z.object({
        id: z.string(),
        updatedAt: z.iso.datetime(),
        legalName: z.string().nullable(),
        entityType: EntityTypeSchema.nullable().optional(),
        vatNumber: z.string().nullable().optional(),
        addressLine1: z.string().nullable().optional(),
        addressLine2: z.string().nullable().optional(),
        addressCity: z.string().nullable().optional(),
        addressProvince: z.string().nullable().optional(),
        addressPostalCode: z.string().nullable().optional(),
        addressCountry: z.string().optional(),
    }).nullable(),
    notes: z.array(LeadNoteEntrySchema),
    archivedAt: z.iso.datetime().nullable(),
    directMarketingConsent: DirectMarketingConsentSchema.nullable(),
    directMarketingConsentDate: z.iso.datetime().nullable(),
    privacyPolicyConsentedAt: z.iso.datetime().nullable(),
});
export const SuperAdminLeadsListQuerySchema = PaginationQuerySchema.extend({
    id: z.uuid().optional(),
    source: LeadSourceSchema.optional(),
    status: z.array(LeadStatusSchema).min(1).optional(),
    businessEntityId: z.uuid().optional(),
    includeArchived: z.coerce.boolean().optional().prefault(false),
});
export const SuperAdminLeadsListResponseSchema = createPaginatedResponseSchema(SuperAdminLeadSchema).extend({ total: z.number() });
const InlineBusinessEntityInputSchema = z.object({
    legalName: z.string().min(1).max(200),
    entityType: EntityTypeSchema.nullable().optional(),
    vatNumber: saVatNumberSchema,
    addressLine1: z.string().max(200).optional().nullable(),
    addressLine2: z.string().max(200).optional().nullable(),
    addressCity: z.string().max(100).optional().nullable(),
    addressProvince: z.string().max(100).optional().nullable(),
    addressPostalCode: z.string().max(20).optional().nullable(),
    addressCountry: z.string().length(2).optional(),
});
export const AdminCreateLeadInputSchema = z.object({
    contactName: z.string().optional(),
    email: z.email().optional(),
    profileUrl: z.url().optional(),
    phone: saPhoneNumberSchema.optional(),
    source: LeadSourceSchema,
    sourceDetail: nullableText(512).optional(),
    status: LeadStatusSchema.optional(),
    businessEntityId: z.uuid().optional(),
    businessEntity: InlineBusinessEntityInputSchema.optional(),
    message: LeadMessageSchema,
    directMarketingConsent: DirectMarketingConsentSchema.nullable().optional(),
    directMarketingConsentDate: z.iso.datetime().optional(),
}).superRefine((val, ctx) => {
    if (val.businessEntityId !== undefined && val.businessEntity !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ['businessEntity'],
            message: 'Provide either businessEntityId (to link an existing entity) or businessEntity (to create a new one), not both',
        });
    }
    if (val.email == null && val.profileUrl == null && val.phone == null) {
        ctx.addIssue({
            code: "custom",
            path: ['email'],
            message: 'A Lead must have an email address, phone number, or LinkedIn profile URL.',
        });
    }
});
export const AdminCreateLeadResponseSchema = SuperAdminLeadSchema.extend(writeWarningsFields);
export const AdminUpdateLeadInputSchema = z.object({
    contactName: z.string().optional(),
    email: z.email().nullable().optional(),
    profileUrl: z.url().nullable().optional(),
    phone: saPhoneNumberSchema.optional(),
    source: LeadSourceSchema.optional(),
    sourceDetail: nullableText(512).optional(),
    status: LeadStatusSchema.optional(),
    businessEntityId: z.uuid().nullable().optional(),
    businessEntity: InlineBusinessEntityInputSchema.optional(),
    directMarketingConsent: DirectMarketingConsentSchema.nullable().optional(),
    directMarketingConsentDate: z.iso.datetime().nullable().optional(),
}).superRefine((val, ctx) => {
    if (val.businessEntityId !== undefined && val.businessEntityId !== null && val.businessEntity !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ['businessEntity'],
            message: 'Provide either businessEntityId (to link an existing entity) or businessEntity (to create a new one), not both',
        });
    }
    if (val.email === null && val.profileUrl === null && val.phone == null) {
        ctx.addIssue({
            code: "custom",
            path: ['email'],
            message: 'A Lead must have an email address, phone number, or LinkedIn profile URL.',
        });
    }
});
export const AdminUpdateLeadResponseSchema = AdminCreateLeadResponseSchema;
export const AppendLeadNoteInputSchema = z.object({ body: z.string().trim().min(1).max(4096) });
export const AppendLeadNoteResponseSchema = SuperAdminLeadSchema;
export const SuperAdminFeedbackAttachmentSchema = z.object({
    index: z.number().int().min(0),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
});
export const SuperAdminFeedbackSchema = z.object({
    id: z.string(),
    userId: z.string(),
    userEmail: z.string(),
    userName: z.string(),
    accountId: z.string().nullable(),
    type: z.string(),
    message: z.string(),
    attachments: z.array(SuperAdminFeedbackAttachmentSchema),
    source: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const SuperAdminFeedbackParamsSchema = z.object({ feedbackId: LowercaseUuidSchema });
export const SuperAdminFeedbackAttachmentParamsSchema = SuperAdminFeedbackParamsSchema.extend({
    attachmentIndex: z.coerce.number().int().min(0),
});
export const SuperAdminFeedbackListQuerySchema = PaginationQuerySchema;
export const FeedbackTotalsByTypeSchema = z.object({
    bug: z.number(),
    idea: z.number(),
    other: z.number(),
});
export const SuperAdminFeedbackListResponseSchema = createPaginatedResponseSchema(SuperAdminFeedbackSchema).extend({
    totalsByType: FeedbackTotalsByTypeSchema,
});
